import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useCallback } from 'react';
import {
    Accordion,
    AccordionItem,
    AccordionItemButton,
    AccordionItemHeading,
    AccordionItemPanel,
    AccordionItemState,
} from 'react-accessible-accordion';
import scrollToElement from 'scroll-to-element';
import shortId from 'shortid';
import get from 'lodash.get';
import flatten from 'lodash.flatten';
import uniq from 'lodash.uniq';
import { updateURLQueryParams } from '../../common/navigation/navigation';
import { APPLICATIONS_GROUPS, IDEL2 } from '../../constants/applications';
import StudentShortSummary from './student-short-summary/StudentShortSummary';
import StudentsListHeader from './StudentsListHeader';
import StudentNonExpandableWrapper from './StudentNonExpandableWrapper';
import { ASSESSMENT_TYPE, SUPPORTED_GRADES_BY_APP } from '../../constants/assessments';
import PMSummary from './pm-summary/PMSummary';
import { useAppContext } from '../../common/contexts/AppContext';
import BMSummary from './bm-summary/BMSummary';
import { getStudentsListWithStatuses } from '../../common/utils/studentsListHelpers';
import { useAppConfigContext } from '../../common/contexts/AppConfigContext';
import useSortedStudents from '../../common/hooks/useSortedStudents';
import { useFeatureToggleContext } from '../../common/contexts/FeatureToggleContext';

const SCROLL_SPEED = 1;

const STUDENT_KEY_SEPARATOR = '/';
const getStudentKey = (studentId, classId) => `${studentId}${STUDENT_KEY_SEPARATOR}${classId}`;

const StudentsList = ({ classId, studentIdFromUrl }) => {
    const {
        bmWindow,
        selectedClassData,
        selectedAppGroup,
        selectedAssessmentType,
    } = useAppContext();
    const { isLecturaCompositeScoreEnabled } = useFeatureToggleContext();

    const { appConfigs, appPmConfigs, isLecturaLicensed } = useAppConfigContext();

    const studentsList = useMemo(
        () => getStudentsListWithStatuses(selectedClassData, selectedAppGroup),
        [selectedAppGroup, selectedClassData],
    );

    const applicationsForRender = APPLICATIONS_GROUPS[selectedAppGroup].filter(value =>
        selectedAssessmentType === ASSESSMENT_TYPE.PM
            ? appPmConfigs.hasOwnProperty(value)
            : appConfigs.hasOwnProperty(value),
    );

    const licensedGradesForApps = useCallback(
        appName => {
            if (appName === IDEL2 && !isLecturaLicensed) {
                return SUPPORTED_GRADES_BY_APP[appName];
            }
            const licensedMeasures = appConfigs?.[appName]?.licensedMeasures ?? [];
            const licensedGrades = uniq(flatten(licensedMeasures.map(item => item.grades)));
            return licensedGrades.length > 0 ? licensedGrades : SUPPORTED_GRADES_BY_APP[appName];
        },
        [appConfigs, isLecturaLicensed],
    );

    const { sortingParams, changeSortingOrder, sortedStudents } = useSortedStudents(
        studentsList,
        Object.keys(appConfigs).filter(
            app => app !== IDEL2 || (isLecturaLicensed && isLecturaCompositeScoreEnabled),
        ),
        selectedAppGroup,
        bmWindow.period,
    );

    const studentUuids = useMemo(
        () =>
            studentsList.reduce(
                (uuids, student) => ({
                    ...uuids,
                    [getStudentKey(student.id, classId)]: shortId.generate(),
                }),
                {},
            ),
        // generate unique ids when class and thus students change
        // we cannot trust studentsList as it's updated by reference on
        // app group switch which is irrelevant for sorting
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [classId],
    );

    const studentRefs = useMemo(
        () =>
            sortedStudents.reduce(
                (refs, student) => ({
                    ...refs,
                    [studentUuids[getStudentKey(student.id, classId)]]: {
                        ref: React.createRef(),
                        expanded: false,
                    },
                }),
                {},
            ),
        // create refs only when class and thus students change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [classId],
    );

    const scrollToStudentDetails = ([studentKey]) => {
        const { ref } = get(studentRefs, studentKey, {});

        if (ref) {
            scrollToElement(ref.current, { ease: 'inOutSine', duration: SCROLL_SPEED });

            const studentIdWithClassId = Object.keys(studentUuids).find(
                key => studentUuids[key] === studentKey,
            );
            const [studentId] = studentIdWithClassId.split(STUDENT_KEY_SEPARATOR);

            studentRefs[studentKey].expanded = true;
            updateURLQueryParams({ classId, classeId: undefined, studentId });
        } else {
            updateURLQueryParams({ classId, classeId: undefined, studentId: undefined });
            studentRefs[studentKey] = { ref: null, expanded: false };
        }
    };

    // HACK: this is the fix for enabling scroll to student details on OWA env.
    // The bug was that OWA sets height: 100% for <html> element and `scroll-to-element` library
    // can't correctly calculate offset needed to scroll to the component.
    useEffect(() => {
        const initialHeight = document.documentElement.style.height;
        document.documentElement.style.height = 'auto';

        return () => {
            document.documentElement.style.height = initialHeight;
        };
    });

    useEffect(() => {
        if (studentIdFromUrl != null) {
            const { ref, expanded } = get(
                studentRefs,
                studentUuids[getStudentKey(studentIdFromUrl, classId)],
                {},
            );
            if (ref) {
                if (expanded) {
                    scrollToElement(ref.current, { ease: 'inOutSine', duration: SCROLL_SPEED });
                } else {
                    ref.current.click();
                }
            }
        }
        // expand student row when student or class changes
        // expanding on app group and assessment type switch is handled by Accordion component itself
        // scroll to expanded row when assessment type or app group changes
        // scroll when results are loaded from store and row height is finalized - react on student list change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classId, studentIdFromUrl, selectedAssessmentType, selectedAppGroup, studentsList]);

    /*
    useEffect(() => {
        const studentListItem = document.querySelectorAll('.students-list-item');
        if (studentListItem.length !== 0) {
            studentListItem.forEach(item => {
                item.setAttribute('tabindex', '-1');
            });
        }
    }, []);
    */

    const renderExpandableItem = (student, studentKey) => (
        <AccordionItem
            className='dashboard-students-list-item'
            key={studentKey}
            data-student-id={student.id}
            uuid={studentUuids[studentKey]}
        >
            <AccordionItemHeading>
                <AccordionItemButton className='dashboard-students-list-item-title'>
                    <StudentShortSummary
                        ref={studentRefs[studentUuids[studentKey]].ref}
                        applicationsForRender={applicationsForRender}
                        student={student}
                        expandable
                    />
                </AccordionItemButton>
            </AccordionItemHeading>

            <AccordionItemState>
                {({ expanded }) => {
                    return (
                        <AccordionItemPanel
                            className={classNames({
                                'dashboard-students-list-item-body': expanded,
                                'students-list-item-body hidden': !expanded,
                            })}
                        >
                            {selectedAssessmentType === ASSESSMENT_TYPE.PM ? (
                                <PMSummary
                                    student={student}
                                    classId={classId}
                                    applications={applicationsForRender}
                                />
                            ) : (
                                <BMSummary
                                    applicationsForRender={applicationsForRender}
                                    classId={classId}
                                    student={student}
                                />
                            )}
                        </AccordionItemPanel>
                    );
                }}
            </AccordionItemState>
        </AccordionItem>
    );

    const renderNonExpandableItem = (student, studentKey) => (
        <StudentNonExpandableWrapper
            key={studentKey}
            studentKey={studentKey}
            studentId={student.id}
            data-student-id={student.id}
            nonExpandableApplicationName={selectedAppGroup}
        >
            <StudentShortSummary
                ref={studentRefs[studentUuids[studentKey]].ref}
                applicationsForRender={applicationsForRender}
                student={student}
            />
        </StudentNonExpandableWrapper>
    );

    return (
        <div className={classNames('dashboard-students-list', 'dashboard-' + selectedAppGroup)}>
            <StudentsListHeader
                selectedAppGroup={selectedAppGroup}
                sortingParams={sortingParams}
                changeSortingOrder={changeSortingOrder}
            />
            <Accordion
                className='dashboard-students-list-body'
                onChange={scrollToStudentDetails}
                allowZeroExpanded
            >
                {sortedStudents.map(student => {
                    const studentKey = getStudentKey(student.id, classId);

                    if (
                        applicationsForRender.some(appName =>
                            licensedGradesForApps(appName).includes(student.grade),
                        )
                    ) {
                        return renderExpandableItem(student, studentKey);
                    }
                    return renderNonExpandableItem(student, studentKey);
                })}
            </Accordion>
        </div>
    );
};

StudentsList.propTypes = {
    classId: PropTypes.string.isRequired,
    studentIdFromUrl: PropTypes.string,
};

StudentsList.defaultProps = {
    studentIdFromUrl: null,
};

export default StudentsList;
